import { useState, type SyntheticEvent } from 'react';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { ThemePalette } from 'mui.theme';
import { type SelectChangeEvent } from '@mui/material';
import { useCustomDropdownEffect } from 'utils/exports/hooks';
import ExportSelector from 'views/FleetOverview/components/FilterViewBar/ExportSelector';
import useAppTheme from 'hooks/useAppTheme';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

interface Props {
  value: number;
  handleChange: (newValue: number) => void;
  handleExport: (val: boolean) => void;
  loadingData: boolean;
  setExportTo?: (val: string) => void;
  exportTo?: string;
  isPrinting?: boolean;
  disabled?: boolean;
  onAiAnalysis?: () => void;
  onLastHoursAnalysis?: (anchor: HTMLElement) => void;
}

const HeaderMenu = ({
  value,
  handleChange,
  handleExport,
  setExportTo,
  exportTo,
  loadingData,
  isPrinting,
  disabled,
  onAiAnalysis,
  onLastHoursAnalysis,
}: Props) => {
  const { t } = useTranslation();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;

  const setHandleChange = (_event: SyntheticEvent, newValue: number) => {
    handleChange(newValue);
  };

  const [exportToLabel, setExportToLabel] = useState(t('export_to'));

  const handleExportDropDownChange = (event: SelectChangeEvent) => {
    (setExportTo as any)(event.target.value);
  };

  useCustomDropdownEffect(
    exportTo as string,
    handleExport,
    setExportToLabel,
    setExportTo as any,
    [exportTo as string]
  );

  return (
    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
      <Tabs
        value={value}
        onChange={setHandleChange}
        aria-label={t('basic_tabs')}
        TabIndicatorProps={{
          sx: {
            backgroundColor: palette.primary,
          },
        }}
      >
        <Tab
          label={t('total_fleet')}
          {...a11yProps(0)}
          style={{
            color: palette.textPrimary,
            textTransform: 'capitalize',
            fontWeight: 'bold',
          }}
        />
        <Tab
          // Updated to use standard translation key for haulers
          label={t('std_asset_haulers')}
          {...a11yProps(1)}
          style={{
            color: palette.textPrimary,
            textTransform: 'capitalize',
            fontWeight: 'bold',
          }}
        />
        <Tab
          // Updated to use standard translation key for loaders
          label={t('std_asset_loaders')}
          {...a11yProps(2)}
          style={{
            color: disabled
              ? ThemePalette.typography.grey
              : palette.textPrimary,
            textTransform: 'capitalize',
            fontWeight: 'bold',
          }}
          disabled={disabled}
        />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'right',
            gap: '8px',
            width: '100%',
            marginTop: '10px',
          }}
        >
          {value !== 2 && onLastHoursAnalysis && (
            <Button
              variant="outlined"
              size="small"
              onClick={(e) => {
                onLastHoursAnalysis?.(e.currentTarget);
              }}
              sx={{
                fontSize: '12px',
                textTransform: 'uppercase',
                fontWeight: 700,
                letterSpacing: '0.08em',
                height: '32px',
                px: '14px',
                whiteSpace: 'nowrap',
                color: palette.primary,
                borderColor: palette.primary,
                '&:hover': {
                  borderColor: palette.primary,
                  background: `${palette.primary}14`,
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <AccessTimeIcon sx={{ fontSize: '15px' }} />
                {t('last_hrs_analysis')}
                <Box
                  component="span"
                  sx={{
                    fontSize: '0.55rem',
                    fontWeight: 800,
                    letterSpacing: '0.06em',
                    backgroundColor: `${palette.primary}26`,
                    color: palette.primary,
                    borderRadius: '4px',
                    px: '5px',
                    py: '1px',
                    border: `1px solid ${palette.primary}`,
                  }}
                >
                  {t('beta')}
                </Box>
              </Box>
            </Button>
          )}
          <Button
            variant="contained"
            size="small"
            onClick={onAiAnalysis}
            sx={{
              position: 'relative',
              overflow: 'hidden',
              fontSize: '12px',
              textTransform: 'uppercase',
              fontWeight: 700,
              letterSpacing: '0.08em',
              height: '32px',
              px: '16px',
              whiteSpace: 'nowrap',
              background: `linear-gradient(135deg, ${palette.primary} 0%, ${palette.primaryDark} 100%)`,
              color: '#fff',
              border: 'none',
              boxShadow: `0 2px 12px ${palette.primary}73`,
              '@keyframes hm-glow': {
                '0%': { boxShadow: `0 2px 10px ${palette.primary}66` },
                '50%': { boxShadow: `0 3px 20px ${palette.primary}b3` },
                '100%': { boxShadow: `0 2px 10px ${palette.primary}66` },
              },
              '@keyframes hm-shimmer': {
                '0%': { left: '-80%' },
                '60%': { left: '130%' },
                '100%': { left: '130%' },
              },
              '@keyframes hm-sparkle': {
                '0%,100%': { opacity: 1, transform: 'scale(1)' },
                '50%': { opacity: 0.5, transform: 'scale(0.85)' },
              },
              animation: 'hm-glow 2.5s ease-in-out infinite',
              '&::after': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: '-80%',
                width: '55%',
                height: '100%',
                background:
                  'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.28) 50%, transparent 100%)',
                animation: 'hm-shimmer 3s ease-in-out infinite',
                pointerEvents: 'none',
              },
              '&:hover': {
                background: `linear-gradient(135deg, ${palette.primaryDark} 0%, ${palette.primary} 100%)`,
                boxShadow: `0 4px 22px ${palette.primary}a6`,
                animation: 'none',
                '&::after': { animation: 'none' },
                '& .ai-sparkle-icon': {
                  animation: 'none',
                  opacity: 1,
                  transform: 'scale(1)',
                },
              },
              '& .ai-sparkle-icon': {
                animation: 'hm-sparkle 1.6s ease-in-out infinite',
                transformOrigin: 'center',
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '7px',
                position: 'relative',
                zIndex: 1,
              }}
            >
              <AutoAwesomeIcon
                className="ai-sparkle-icon"
                sx={{ fontSize: '16px' }}
              />
              {t('ai_analysis')}
              <Box
                component="span"
                sx={{
                  ml: '6px',
                  fontSize: '0.55rem',
                  fontWeight: 800,
                  letterSpacing: '0.06em',
                  backgroundColor: 'rgba(255,255,255,0.22)',
                  borderRadius: '4px',
                  px: '5px',
                  py: '1px',
                  verticalAlign: 'middle',
                }}
              >
                {t('beta')}
              </Box>
            </Box>
          </Button>
          <ExportSelector
            view={'list'}
            exportToLabel={exportToLabel}
            exportTo={exportTo as string}
            onExportToChange={handleExportDropDownChange}
            enableDarkTheme={enableDarkTheme}
            showComponent={loadingData}
            isPrinting={isPrinting}
          />
        </Box>
      </Tabs>
    </Box>
  );
};

export default HeaderMenu;
